import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Paper,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  ArrowForward as ArrowForwardIcon,
  Celebration as CelebrationIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  SchoolOutlined as SchoolOutlinedIcon,
} from '@mui/icons-material'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { OnboardingHeader } from '@/components/placement/OnboardingHeader'
import { ExerciseRenderer } from '@/components/exercises/renderers/ExerciseRenderer'
import {
  GOALS_WITH_DEADLINE,
  LEARNING_GOALS,
  PROFILE_LEVELS,
  PROFILE_SKILLS,
  languageProfileApi,
  placementApi,
  type PlacementRun,
  type PlacementRunSummary,
  type PlacementRunItem,
  type ProfileLevel,
  type SkillMeasurement,
  type LearningGoal,
  type PlacementStage,
  type PreferredSlot,
  type SelfAssessmentPayload,
  type StudentLanguageProfile,
} from '@/api/placement'
import { authApi } from '@/api/auth'
import { rememberManualLogout } from '@/api/authDiagnostics'
import { detachWebPushBeforeLogout } from '@/hooks/useWebPush'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'
import { PROBE_SKILLS, skillMeasurementLabel } from '@/utils/placementLabels'

/** Порядок ступеней для шагового индикатора. */
/**
 * Ответ заполнен, а не просто заведён пустым объектом.
 *
 * Рендерер кладёт ключ в ответ уже при первом касании поля, поэтому проверяем
 * значения: пустая строка, пустой список и пустая карта — это «не отвечено».
 */
function isFilled(answer: Record<string, unknown> | undefined): boolean {
  if (answer === undefined) return false

  return Object.values(answer).some((value) => {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') return Object.keys(value as object).length > 0

    return true
  })
}

function draftKeyFor(runId: number, blockNumber: number): string {
  return `placement-draft-${runId}-${blockNumber}`
}

function readDraft(runId: number, blockNumber: number): Record<number, Record<string, unknown>> | null {
  try {
    const raw = window.localStorage.getItem(draftKeyFor(runId, blockNumber))

    return raw === null ? null : (JSON.parse(raw) as Record<number, Record<string, unknown>>)
  } catch {
    return null
  }
}

function clearDraft(key: string | null): void {
  if (key === null) return

  try {
    window.localStorage.removeItem(key)
  } catch {
    // Хранилище недоступно — черновика и не было.
  }
}

/**
 * Убрать черновики чужих прогонов и прошлых блоков.
 *
 * Ключ содержит номер прогона и блока, поэтому без уборки хранилище копит по
 * записи на каждый пройденный блок и каждую пересдачу.
 */
function sweepDrafts(keep: string | null): void {
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('placement-draft-') && key !== keep)
      .forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Хранилище недоступно — убирать нечего.
  }
}

const STAGES = ['goals', 'adaptive', 'probes'] as const

/**
 * Сколько примерно осталось до конца теста, в минутах.
 *
 * Оценка грубая и честно названа примерной: сколько блоков выдаст адаптивный
 * алгоритм, заранее не знает никто. Но «ещё минут пять» и «непонятно сколько»
 * — разные решения для человека, который думает, начинать ли сейчас, и
 * молчать здесь дороже, чем ошибиться на минуту.
 */
const SECONDS_PER_ITEM = 40
const SECONDS_PER_PROBE = 180
/** Сколько блоков закрытых заданий обычно остаётся после текущего. */
const BLOCKS_AHEAD = 1
const ITEMS_PER_BLOCK = 6
const PROBES_EXPECTED = 2

function estimateMinutesLeft(
  stage: PlacementStage,
  itemsLeft: number,
  totalItems: number,
): number | null {
  const probes = PROBES_EXPECTED * SECONDS_PER_PROBE

  if (stage === 'goals') {
    const blocks = (BLOCKS_AHEAD + 1) * ITEMS_PER_BLOCK * SECONDS_PER_ITEM

    return Math.round((blocks + probes) / 60)
  }

  if (stage === 'probes') {
    return itemsLeft === 0 ? null : Math.round((itemsLeft * SECONDS_PER_PROBE) / 60)
  }

  // Незаполненные задания текущего блока плюс ожидаемый следующий блок: длину
  // следующего берём по текущему, а не по константе — так оценка подстраивается
  // под то, что алгоритм выдаёт на самом деле.
  const ahead = BLOCKS_AHEAD * (totalItems > 0 ? totalItems : ITEMS_PER_BLOCK)

  return Math.max(1, Math.round(((itemsLeft + ahead) * SECONDS_PER_ITEM + probes) / 60))
}

/**
 * Первая ступень спрашивает только самооценку.
 *
 * Цель, детали и желаемое расписание переехали в анкету ученика: они не
 * зависят от уровня, меняются в любой момент и нужны учителю независимо от
 * теста. Спрашивать их здесь значило запирать тест за анкетой, которую ученик
 * пришёл пропустить, и заново переспрашивать на каждой пересдаче.
 */
type GoalsForm = {
  goal: LearningGoal | ''
  goalDetails: string
  goalDeadline: string
  daysPerWeek: string
  minutesPerDay: string
  slots: PreferredSlot[]
  interests: string
  selfLevel: ProfileLevel | ''
}

const EMPTY_GOALS: GoalsForm = {
  goal: '',
  goalDetails: '',
  goalDeadline: '',
  daysPerWeek: '',
  minutesPerDay: '',
  slots: [],
  interests: '',
  selfLevel: '',
}

/** Сколько минут длится занятие — из чего ученику предлагают выбрать. */
const MINUTES_PER_DAY_OPTIONS = [15, 30, 45, 60, 90, 120]

/** Дни недели в порядке календаря: 1 — понедельник, как в шаблонах расписания. */
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

/** Столько же пожеланий принимает сервер. */
const MAX_PREFERRED_SLOTS = 14

/**
 * Интересы: строка из поля — в список для сервера.
 *
 * Ввод свободный через запятую, а не выбор из справочника: список тем, о
 * которых человеку интересно говорить, конечным не бывает, и «другое» в нём
 * оказывалось бы самым частым ответом.
 */
function parseInterests(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '')
    .slice(0, 20)
    .map((item) => item.slice(0, 64))
}

/**
 * Прохождение теста уровня учеником.
 *
 * Три ступени по возрастанию точности: анкета, адаптивный блок закрытых
 * заданий, пробы письма и говорения. Уйти можно на любой: выход закрывает тест
 * и переносит измеренное в профиль — но и запирает пересдачу на месяц, как
 * завершение, поэтому спрашивается подтверждение.
 *
 * Промежуточный результат ученику НЕ показывается: узнав «пока B1», он меняет
 * поведение, и следующий блок померит уже не язык. Уровни появляются только
 * после завершения прогона.
 */
/**
 * «Это не экзамен» — второй акцент экрана после самого уровня.
 *
 * Тон важнее размера: ученик, прочитавший цифру как оценку, идёт спорить с
 * ней вместо того, чтобы заниматься. Мелкой сноской это не читалось.
 */
function FairPlayNote() {
  const { t } = useTranslation()

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderWidth: 2,
        borderColor: 'info.main',
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
      }}
    >
      <Typography variant="h6" gutterBottom>
        {t('placement.fairPlayTitle')}
      </Typography>
      <Typography variant="body1">{t('placement.fairPlay')}</Typography>
    </Paper>
  )
}

/**
 * Первый экран того, кто зашёл впервые.
 *
 * Он видит не «тест уровня», а незнакомый сайт, поэтому сначала — что это за
 * сервис и что сейчас произойдёт, и только потом кнопка. Три коротких пункта
 * вместо абзаца: человек читает их за пять секунд и понимает, во что ввязался.
 *
 * Про возможность пропустить сказано сразу и здесь же — почему лучше не
 * пропускать. Прятать это значило бы делать вид, что выбора нет, а он есть.
 */
function OnboardingWelcome({ t }: { t: TFunction }) {
  const steps = [
    { key: 'goal', minutes: t('placement.welcomeStepGoalTime') },
    { key: 'test', minutes: t('placement.welcomeStepTestTime') },
    { key: 'result', minutes: '' },
  ] as const

  return (
    <Stack spacing={3}>
      <Typography variant="h6">{t('placement.welcomeLead')}</Typography>

      <Stack spacing={2}>
        {steps.map((step, index) => (
          <Stack key={step.key} direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 700,
              }}
            >
              {index + 1}
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {t(`placement.welcomeStep.${step.key}.title`)}
                {step.minutes !== '' && (
                  <Typography component="span" variant="subtitle1" color="text.secondary">
                    {' · '}
                    {step.minutes}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t(`placement.welcomeStep.${step.key}.text`)}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Alert severity="info" icon={false}>
        <Typography variant="body1">{t('placement.welcomeSkip')}</Typography>
      </Alert>
    </Stack>
  )
}

/**
 * Появление эмблемы.
 *
 * Одна анимация на весь экран, и та выключается системной настройкой: праздник
 * — не повод укачивать человека, который просил анимации не показывать.
 */
const FINISH_POP = {
  animation: 'placementFinishPop 420ms cubic-bezier(0.34, 1.4, 0.64, 1) both',
  '@keyframes placementFinishPop': {
    from: { transform: 'scale(0.7)', opacity: 0 },
    to: { transform: 'scale(1)', opacity: 1 },
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}

interface OnboardingFinishProps {
  /** Общий уровень; null — намерить не удалось, и врать об этом нельзя. */
  overallLevel: string | null
  /** Навык → уровень. У письма и устной речи его нет: уровень ставит человек. */
  measuredLevels: Record<string, ProfileLevel | null> | null
  /** Разбор по навыкам: отличает «проверили» от «мерить было нечем». */
  measurements: Record<string, SkillMeasurement>
  /** Уровень в профиле правил преподаватель — приписывать его тесту нельзя. */
  levelSetByTeacher: boolean
  /** Имя для обращения; пустая строка — поздравляем безлично. */
  name: string
  onGoToDashboard: () => void
}

/**
 * Последний экран мастера онбординга.
 *
 * Парный к {@link OnboardingWelcome}: тот объясняет, во что человек ввязывается,
 * этот — что всё получилось. Отсюда разница в наполнении: ни оговорки «это не
 * экзамен», ни срока пересдачи, ни пересказа ступеней здесь нет. Всё это нужно
 * было ДО теста; после теста оно превращает финиш в инструкцию и уводит взгляд
 * от единственного действия, которое человеку осталось.
 *
 * Экран держит две мысли: «получилось» и «дальше туда». Верх — поздравление и
 * результат, низ — отдельная полоса с одной большой кнопкой.
 *
 * Счёт «решено верно», чипы по навыкам и `skillMeasurementLabel` сюда не
 * переехали намеренно: это отчёт, и его место — страница теста в кабинете.
 * Здесь то же самое сказано двумя строками прозой, без кодов уровней.
 *
 * `t` берём хуком, а не пропом (как {@link FairPlayNote} в этом же файле):
 * нужен ещё и `i18n.language` для названий навыков в нижнем регистре.
 */
function OnboardingFinish({
  overallLevel,
  measuredLevels,
  measurements,
  levelSetByTeacher,
  name,
  onGoToDashboard,
}: OnboardingFinishProps) {
  const { t, i18n } = useTranslation()

  // Праздничная половина включается только тогда, когда есть что праздновать:
  // хлопушка над «уровень не определили» звучит издёвкой.
  const celebrating = overallLevel !== null

  // «Проверили» — навык, о котором тест хоть что-то сказал: точный уровень или
  // хотя бы граница («не выше B2»). Границу молча прятать нельзя — человек эти
  // задания решал. `not_testable` сюда не попадает: заданий навыка в банке нет,
  // и отчитываться перед учеником за устройство банка незачем.
  const checked = PROFILE_SKILLS.filter((skill) => {
    if (PROBE_SKILLS.includes(skill)) return false

    // Промежуточная переменная, а не `measurements[skill]?.state`: тип карты
    // не допускает undefined, и прямое сравнение с ним TS считает ошибкой.
    const measurement: SkillMeasurement | undefined = measurements[skill]

    // У профилей, измеренных до появления разбора, разбора нет — там
    // признаком остаётся сам уровень.
    return measurement === undefined
      ? measuredLevels?.[skill] != null
      : measurement.state === 'measured' || measurement.state === 'bounded'
  })

  // Письмо и устная речь: пока преподаватель не оценил — уровня нет. Считаем по
  // данным, а не по константе: экран открывается и после перезагрузки, когда
  // часть работ уже могла быть проверена.
  const awaiting = PROBE_SKILLS.filter((skill) => measuredLevels?.[skill] == null)

  /**
   * Навыки в строку, в нижнем регистре.
   *
   * Из i18n они приходят с заглавной, а стоят в середине фразы. Склонять их
   * нельзя, поэтому обе строки построены через двоеточие — после него
   * именительный падеж уместен.
   */
  const skillList = (skills: string[]) =>
    skills
      .map((skill) => t(`placement.skills.${skill}`).toLocaleLowerCase(i18n.language))
      .join(', ')

  // Расшифровку берём только для известной ступени: на неожиданном значении
  // i18next вернул бы в вёрстку сам ключ.
  const hasBlurb = celebrating && (PROFILE_LEVELS as string[]).includes(overallLevel)

  return (
    <Paper
      variant="outlined"
      // Узкая карточка по центру: контейнер мастера — 1200px, и горстка текста
      // растянулась бы на мониторе во всю ширину. Радиус не задаём — у Paper он
      // уже 8px из темы, свой сделал бы карточку не такой, как все остальные.
      sx={{ maxWidth: 640, mx: 'auto', overflow: 'hidden' }}
    >
      {/* Верх — поздравление и результат */}
      <Box
        sx={{
          // Шкала отступов в теме табличная (3 = 12px, 4 = 16, 7 = 32, 8 = 40),
          // поэтому числа крупнее привычных: с дефолтной 8×n экран вышел бы
          // вдвое теснее. Дробные значения (1.5) в таблицу не попадают и молча
          // падают в fallback — их здесь нет.
          px: { xs: 4, sm: 7 },
          py: { xs: 7, sm: 8 },
          textAlign: 'center',
          // Мягкая заливка вместо картинки: работает в обеих темах, ничего не
          // весит и не спорит с уровнем за внимание.
          background: (theme) =>
            `radial-gradient(120% 120% at 50% 0%, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0)} 70%)`,
        }}
      >
        <Stack spacing={{ xs: 5, sm: 6 }} alignItems="center">
          {/* Эмблема. Цвет иконке задаём явным `inherit`: тема красит все
              MuiSvgIcon в свой iconPrimary (в светлой — почти чёрный), и
              унаследованный от круга contrastText она бы перебила */}
          <Box
            sx={{
              ...FINISH_POP,
              width: 72,
              height: 72,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...(celebrating
                ? {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.32)}`,
                  }
                : // Мерить было нечего: композиция та же, но без праздничного
                  // пятна — иначе экран радуется вместо человека.
                  {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    color: 'text.secondary',
                  }),
            }}
          >
            {celebrating ? (
              <CelebrationIcon sx={{ fontSize: 36, color: 'inherit' }} />
            ) : (
              <SchoolOutlinedIcon sx={{ fontSize: 36, color: 'inherit' }} />
            )}
          </Box>

          <Box>
            {/* h1 экрана: заголовок страницы на финише скрыт — «Добро
                пожаловать в EasyA» над поздравлением читается как возврат в
                начало. Размер и вес — как у всех h1 проекта */}
            <Typography variant="h4" component="h1" fontWeight={700}>
              {name !== '' ? t('placement.finishTitleNamed', { name }) : t('placement.finishTitle')}
            </Typography>
            {/* Подзаголовок различает ветки: «теперь мы знаем, с чего начать»
                неправда там, где уровень определить не удалось */}
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              {celebrating ? t('placement.finishSubtitle') : t('placement.finishSubtitleNoLevel')}
            </Typography>
          </Box>

          {celebrating ? (
            <Stack spacing={4} alignItems="center">
              {/* Обычным регистром, а не overline: «ВАШ УРОВЕНЬ» капсом на
                  кириллице читается хуже строчных. Ключ переиспользован —
                  такой уже есть на странице теста */}
              <Typography variant="body2" color="text.secondary">
                {t('placement.overallLevelTitle')}
              </Typography>

              <Box
                sx={{
                  width: { xs: 120, sm: 132 },
                  height: { xs: 120, sm: 132 },
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 3,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }}
              >
                {/* Размер явный: в теме h1 = 24px, h2 = 20px, и ни один
                    заголовочный вариант крупную цифру не даёт */}
                <Typography
                  component="span"
                  fontWeight={700}
                  lineHeight={1}
                  sx={{ fontSize: { xs: 44, sm: 52 } }}
                >
                  {overallLevel}
                </Typography>
              </Box>

              {/* Одна фраза о том, что эта буква значит: голая «A2» ничего не
                  говорит тому, кто пришёл с лендинга, и читается как оценка */}
              {hasBlurb && (
                <Typography variant="body1">
                  {t(`placement.finishLevelBlurb.${overallLevel}`)}
                </Typography>
              )}

              {/* Правка учителя сильнее теста: промолчать значило бы приписать
                  тесту чужую цифру и оставить вопрос «почему не как в тесте» */}
              {levelSetByTeacher && (
                <Typography variant="body2" color="text.secondary">
                  {t('placement.finishTeacherLevel')}
                </Typography>
              )}
            </Stack>
          ) : (
            // Мерить было нечего — говорим прямо и виним себя, а не ученика.
            // Красного алерта здесь нет: человек ничего не сломал.
            <Stack spacing={2} alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                {t('placement.finishNoLevelTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('placement.finishNoLevelText')}
              </Typography>
            </Stack>
          )}

          {/* Две строки прозой вместо пяти чипов: чип «Аудирование A2» — это
              отчёт, а «что уже проверили» — рассказ. В ветке без уровня строк
              нет: там про преподавателя уже сказано выше, и повтор лишний */}
          {celebrating && (checked.length > 0 || awaiting.length > 0) && (
            <Stack spacing={2}>
              {checked.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('placement.finishChecked', { skills: skillList(checked) })}
                </Typography>
              )}
              {awaiting.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('placement.finishTeacherNext', { skills: skillList(awaiting) })}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Низ — полоса действия. Отделена бордюром намеренно: кнопка в общем
          потоке текста теряется, а это единственное, что человеку осталось */}
      <Box
        sx={{
          px: { xs: 4, sm: 7 },
          py: { xs: 6, sm: 7 },
          textAlign: 'center',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Stack spacing={4} alignItems="center">
          {/* Будущее время намеренно: ученик мог прийти с лендинга сам, и
              договора у него ещё нет — обещать готовое расписание нельзя */}
          <Typography variant="body1" fontWeight={600}>
            {t('placement.finishNext')}
          </Typography>

          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={onGoToDashboard}
            sx={{
              // Тема задаёт кнопке жёсткие 40px высоты, и без height:'auto'
              // никакой padding крупной её не сделает — размер `large` тоже.
              height: 'auto',
              minHeight: 52,
              px: 7,
              py: 3,
              // На телефоне во всю ширину: промахнуться мимо единственного
              // действия экрана человек не должен.
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('placement.finishCta')}
          </Button>

          {/* Снимает страх «а вдруг я что-то потеряю, если уйду» */}
          <Typography variant="caption" color="text.secondary">
            {t('placement.finishSaved')}
          </Typography>
        </Stack>
      </Box>
    </Paper>
  )
}

export type PlacementTestVariant = 'dashboard' | 'onboarding'

interface PlacementTestPageProps {
  /**
   * `onboarding` — полноэкранный мастер без бокового меню: первый экран
   * ученика, пришедшего сам. Уйти из него можно только явной кнопкой, чтобы
   * человек не проваливался в кабинет случайным кликом по навигации.
   */
  variant?: PlacementTestVariant
}

export function PlacementTestPage({ variant = 'dashboard' }: PlacementTestPageProps = {}) {
  const { t } = useTranslation()
  const currentUser = useAuthStore((state) => state.user)
  const clearOnboardingGate = useOnboardingStore((state) => state.clear)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()
  const fullscreen = variant === 'onboarding'

  const [run, setRun] = useState<PlacementRun | null>(null)
  /** Уровни из профиля: экран результата иначе живёт только до перезагрузки. */
  const [profile, setProfile] = useState<StudentLanguageProfile | null>(null)
  const [retestAt, setRetestAt] = useState<string | null>(null)
  /** Последний закрытый прогон: из него берётся счёт «решено верно». */
  const [lastRun, setLastRun] = useState<PlacementRunSummary | null>(null)
  const [items, setItems] = useState<PlacementRunItem[]>([])
  const [answers, setAnswers] = useState<Record<number, Record<string, unknown>>>({})
  const [goals, setGoals] = useState<GoalsForm>(EMPTY_GOALS)

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [leaveOpen, setLeaveOpen] = useState(false)

  /**
   * Ответы блока живут на клиенте до общей сдачи, поэтому дублируются в
   * браузер: перезагрузка вкладки посреди блока иначе стирает всё, что человек
   * уже написал, и заставляет решать заново.
   */
  const draftKey = run === null ? null : `placement-draft-${run.id}-${run.block_number}`

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const state = await placementApi.state()

      // Черновик читается ДО того, как прогон попадёт в состояние: ключ
      // черновика собирается из прогона, и эффект сохранения, сработав на
      // пустых ответах, затёр бы его раньше, чем мы успели прочитать.
      const saved = state.run === null
        ? null
        : readDraft(state.run.id, state.run.block_number)

      sweepDrafts(
        state.run === null ? null : draftKeyFor(state.run.id, state.run.block_number),
      )

      setAnswers(saved ?? {})
      setRun(state.run)
      setRetestAt(state.retest_available_at)

      // Условие по статусу, а не по ступени: протухший прогон возвращается со
      // своей ступенью и статусом «брошен», и запрос блока по нему встречал
      // вернувшегося через неделю ученика красной ошибкой вместо кнопки
      // «Начать тест».
      if (
        state.run?.status === 'in_progress'
        && (state.run.stage === 'adaptive' || state.run.stage === 'probes')
      ) {
        // Ошибка выдачи блока не глотается: пустой список неотличим от
        // «заданий нет», и поломка сервера выглядела бы штатным экраном.
        setItems(await placementApi.block(state.run.id))
        setProfile(null)
      } else {
        setItems([])

        // Вне прохождения показываем измеренное из профиля: экран результата
        // держался на ответе одного запроса и пропадал при перезагрузке, а
        // своей страницы профиля у ученика нет.
        if (currentUser !== null) {
          const [profileRow, runRows] = await Promise.all([
            languageProfileApi.show(currentUser.id).catch(() => null),
            // Счёт по навыкам живёт в прогоне, а закрытый прогон в «текущих»
            // не возвращается — после перезагрузки строка «решено верно»
            // исчезала вместе с ним, хотя объясняет она именно результат.
            languageProfileApi.runs(currentUser.id).catch(() => []),
          ])

          setProfile(profileRow)
          setLastRun(runRows[0] ?? null)

          // Мастер отработал: гейт снимаем сразу, не дожидаясь следующего
          // запроса профиля, — иначе после завершения теста ученика бросало
          // бы обратно в онбординг при первом же переходе.
          if (profileRow !== null && !profileRow.needs_onboarding) {
            clearOnboardingGate()
          }
        }
      }
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setLoading(false)
    }
  }, [clearOnboardingGate, currentUser, t])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (draftKey === null) return

    try {
      window.localStorage.setItem(draftKey, JSON.stringify(answers))
    } catch {
      // Приватное окно или запрет на хранилище: черновик — подстраховка,
      // а не условие работы теста.
    }
  }, [answers, draftKey])

  const activeStage = run === null ? 0 : STAGES.indexOf(run.stage)

  /**
   * Уровни для экрана результата: свежие из прогона, иначе из профиля.
   *
   * Прогон отдаёт их только сразу после завершения, профиль — всегда; после
   * перезагрузки страницы виден именно профиль.
   */
  const measuredLevels = useMemo<Record<string, ProfileLevel | null> | null>(() => {
    if (run?.status === 'completed' && run.computed_levels !== null) {
      return run.computed_levels
    }

    return profile !== null && profile.has_level ? profile.skill_levels : null
  }, [profile, run])

  /** Разбор по навыкам: у свежего прогона свой, иначе из профиля. */
  const measurements = useMemo<Record<string, SkillMeasurement>>(
    () => run?.skill_measurements ?? profile?.skill_measurements ?? {},
    [profile, run],
  )

  /**
   * Счёт «верных из выданных» — только по этому прогону.
   *
   * В профиль он не переносится по замыслу: профиль отвечает на «какой уровень
   * у ученика», а не «как он сдавал в прошлый раз». Пустые навыки отсеиваются —
   * строка «0 из 0» ничего не сообщает.
   */
  const scoreRows = useMemo(
    () =>
      Object.entries(run?.skill_scores ?? lastRun?.skill_scores ?? {}).filter(
        ([, score]) => score.total > 0,
      ),
    [lastRun, run],
  )

  /**
   * Общий уровень: из завершённого прогона либо из профиля.
   *
   * Прогон отдаёт разбивку по навыкам, но не общий, поэтому у свежего
   * результата он считается тем же правилом, что и на сервере, — медианой
   * измеренных.
   */
  const overallLevel = useMemo<string | null>(() => {
    if (profile?.overall_level != null) return profile.overall_level

    const measured = Object.values(run?.computed_levels ?? {}).filter(
      (level): level is ProfileLevel => level !== null,
    )

    if (measured.length === 0) return null

    const ordered = [...measured].sort(
      (a, b) => PROFILE_LEVELS.indexOf(a) - PROFILE_LEVELS.indexOf(b),
    )

    // Медиана, при чётном числе — нижняя из двух средних: как в движке.
    return ordered[Math.floor((ordered.length - 1) / 2)]
  }, [profile, run])

  /**
   * Сколько заданий блока заполнено.
   *
   * Считается по ответам на клиенте, а не по отметке сервера: задания больше
   * не сдаются по одному — блок уходит целиком, и до этого момента сервер о
   * них ничего не знает.
   */
  const filledCount = useMemo(
    () => items.filter((item) => isFilled(answers[item.id])).length,
    [answers, items],
  )

  /**
   * Отказаться от мастера и уйти в кабинет.
   *
   * Отказ окончательный: мастер этого ученика больше не поймает. Прогон при
   * этом не бросается — он живёт на сервере, и доделать тест можно из раздела
   * «Тест уровня»; поэтому кнопка называется «Пропустить», а не «Продолжить
   * позже», и рядом с ней стоит подсказка, что именно пропускается.
   */
  const leaveOnboarding = async () => {
    if (currentUser !== null) {
      try {
        await useOnboardingStore.getState().skip(currentUser.id)
      } catch (err) {
        // Отметка не сохранилась — значит после перезагрузки мастер вернётся.
        // Молчать об этом нельзя: человек решит, что кнопка сломана.
        setError(displayErrorMessage(getErrorMessage(err), t('common.error')))

        return
      }
    }

    navigate(ROUTES.DASHBOARD)
  }

  /**
   * Уйти в кабинет, ничего не записывая.
   *
   * Штатный конец мастера: тест пройден, гейт снят сам, и записывать ученику
   * «отказался от онбординга» здесь было бы неправдой.
   */
  const goToDashboard = () => navigate(ROUTES.DASHBOARD)

  // Ручной выход, как в меню кабинета: сервер пишет logout в журнал, а
  // следующий вход покажет prior_state=manual
  const handleLogout = async () => {
    try {
      await detachWebPushBeforeLogout()
      await authApi.logout()
    } catch {
      // Сессия могла уже закончиться — выходим локально
    } finally {
      rememberManualLogout()
      logout()
    }
  }

  /**
   * Мастер доведён до конца — вместо страницы теста показываем финиш.
   *
   * Только завершённый прогон. У брошенного статус тоже «не in_progress», а
   * «Уйти без сохранения» вызывает abandon() и перезагрузку без навигации:
   * человек, секунду назад отказавшийся от теста, получил бы поздравление, а
   * вместе со старой карточкой потерял бы кнопку «Начать тест» — уйти из
   * мастера стало бы некуда, кроме кабинета.
   *
   * `run === null` — это перезагрузка страницы уже после финиша: закрытые
   * прогоны сервер в состоянии не возвращает, и уровни приходят из профиля.
   */
  const wizardFinished
    = fullscreen
    && !loading
    && (run?.status === 'completed' || (run === null && measuredLevels !== null))

  /**
   * Имя для поздравления — только первое слово.
   *
   * «Поздравляем, Иванов Иван Петрович!» — не поздравление. Почта в запас не
   * берётся: обращение по адресу хуже безличного «Поздравляем!».
   */
  const studentName = (currentUser?.first_name ?? currentUser?.name ?? '').trim().split(' ')[0]

  /** Сколько минут осталось примерно; null — оценивать нечего. */
  const minutesLeft = useMemo(() => {
    if (run === null || run.status !== 'in_progress') return null

    return estimateMinutesLeft(run.stage, items.length - filledCount, items.length)
  }, [filledCount, items.length, run])

  const withBusy = async (action: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setBusy(false)
    }
  }

  const start = () =>
    withBusy(async () => {
      setRun(await placementApi.start())
    })

  const submitGoals = () =>
    withBusy(async () => {
      if (run === null) return

      const interests = parseInterests(goals.interests)

      const payload: SelfAssessmentPayload = {
        goal: goals.goal === '' ? null : goals.goal,
        goal_details: goals.goalDetails.trim() === '' ? null : goals.goalDetails.trim(),
        // Срок уходит только у цели, у которой он бывает: у остальных сервер
        // отбивает его 422, и «разговорная практика до 1 июня» не сохранится.
        goal_deadline:
          goals.goal !== '' && GOALS_WITH_DEADLINE.includes(goals.goal) && goals.goalDeadline !== ''
            ? goals.goalDeadline
            : null,
        days_per_week: goals.daysPerWeek === '' ? null : Number(goals.daysPerWeek),
        minutes_per_day: goals.minutesPerDay === '' ? null : Number(goals.minutesPerDay),
        // Пустые строки времени не отправляем: сервер ждёт HH:MM, а
        // недозаполненная строка — это «человек начал и передумал».
        preferred_slots: goals.slots.filter((slot) => slot.time !== ''),
        interests,
        self_assessed_level: goals.selfLevel === '' ? null : goals.selfLevel,
      }

      const updated = await placementApi.submitSelfAssessment(run.id, payload)
      setRun(updated)
      setItems(await placementApi.block(updated.id))
      setAnswers({})
    })

  /**
   * Сдать блок целиком и пойти дальше.
   *
   * Одной кнопкой, а не по заданию: человек заполняет весь блок и идёт дальше,
   * а сдавать каждое задание отдельно он забывает — и упирался в «блок отвечен
   * не полностью», не понимая, какое именно задание недоделано.
   *
   * Уже сданные пропускаются: если сдача сорвалась на середине, повтор не
   * должен натыкаться на «попытка не активна» по первым заданиям.
   */
  const submitBlock = (then: () => Promise<void>) =>
    withBusy(async () => {
      for (const item of items) {
        if (item.answered) continue

        const updated = await placementApi.submitItem(item.id, answers[item.id] ?? {})

        setItems((current) => current.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)))
      }

      clearDraft(draftKey)

      await then()
    })

  /** Спросить про незаполненные и сдать блок. */
  const finishBlock = (then: () => Promise<void>) => {
    const skipped = items.length - filledCount

    if (skipped > 0 && !window.confirm(t('placement.skippedConfirm', { count: skipped }))) {
      return
    }

    void submitBlock(then)
  }

  /**
   * Продвинуться после сдачи блока.
   *
   * Без обёртки в busy: её держит {@link submitBlock}, из которого этот шаг и
   * вызывается — блок и переход теперь одно действие.
   */
  const advanceAfterBlock = async () => {
    if (run === null) return

    const updated = await placementApi.advance(run.id)

    setRun(updated)
    setAnswers({})

    if (updated.status !== 'in_progress') {
      // Прогон закрылся сам: держим его же, а срок пересдачи спрашиваем
      // отдельно — перечитывание состояния вернуло бы пустоту вместо
      // результата, открытых прогонов там больше нет.
      setItems([])
      setRetestAt((await placementApi.state()).retest_available_at)

      return
    }

    // На пробах блок тот же запрос: сервер выдаёт письмо и говорение как
    // обычные позиции прогона.
    setItems(await placementApi.block(updated.id))
  }

  const completeAfterBlock = async () => {
    if (run === null) return

    // Закрытый прогон держим из ответа: список текущих его уже не отдаёт —
    // там только открытые, — а результат ученик видит только здесь, своей
    // страницы профиля у него нет.
    const finished = await placementApi.complete(run.id)

    setRun(finished)
    setItems([])
    setAnswers({})

    // Профиль нужен, чтобы не соврать: при правке учителя результат теста в
    // него не попадает, и «уровень записан» было бы неправдой.
    if (currentUser !== null) {
      setProfile(await languageProfileApi.show(currentUser.id).catch(() => null))
    }

    // Срок повторной сдачи считает сервер, и со старым значением экран
    // предлагал «Начать тест» сразу после завершения — клик отвечал ошибкой.
    setRetestAt((await placementApi.state()).retest_available_at)
  }

  /** Сохранить результат и уйти: сдать заполненное и закрыть прогон. */
  const saveAndLeave = () => {
    setLeaveOpen(false)

    void submitBlock(completeAfterBlock)
  }


  const abandon = () =>
    withBusy(async () => {
      if (run === null) return

      setLeaveOpen(false)
      clearDraft(draftKey)

      await placementApi.abandon(run.id)
      await load()
    })


  const content = (
      <Box sx={{ p: 3, maxWidth: fullscreen ? 1200 : undefined, mx: fullscreen ? 'auto' : undefined }}>
        {/* В полноэкранном мастере навигации нет намеренно: это первый экран
            ученика, и хлебные крошки уводили бы его в кабинет, которого он
            ещё не видел. Выйти можно явной кнопкой в шапке */}
        {!fullscreen && (
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
            <Link
              to={ROUTES.DASHBOARD}
              style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'inherit' }}
            >
              <HomeIcon fontSize="small" />
              {t('dashboard.home')}
            </Link>
            <Typography color="text.primary">{t('placement.title')}</Typography>
          </Breadcrumbs>
        )}

        {/* На финише заголовка нет: «Добро пожаловать» над «Поздравляем!» —
            второе приветствие подряд и намёк, что человека вернули в начало.
            На время загрузки в мастере он тоже скрыт, иначе успевает мигнуть */}
        {!(fullscreen && (loading || wizardFinished)) && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" component="h1" fontWeight={700}>
                {fullscreen ? t('placement.onboardingTitle') : t('placement.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fullscreen ? t('placement.onboardingSubtitle') : t('placement.subtitle')}
              </Typography>
            </Box>
            {!fullscreen && <PageHelpPopover pageKey="placement" />}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && (
          <>
            {/* В мастере скелетон замещает и скрытый заголовок — иначе экран
                подпрыгивает, когда данные приходят */}
            {fullscreen && <Skeleton variant="text" width={320} height={64} sx={{ mb: 2 }} />}
            <Card>
              <CardContent>
                <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={180} />
              </CardContent>
            </Card>
          </>
        )}

        {!loading && run !== null && run.status === 'in_progress' && (
          <Box sx={{ mb: 3 }}>
            <Stepper activeStep={activeStage}>
              {STAGES.map((stage) => (
                <Step key={stage}>
                  <StepLabel>{t(`placement.stages.${stage}`)}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* «Ещё минут пять» и «непонятно сколько» — разные решения для
                того, кто думает, начинать ли сейчас. Оценка названа примерной
                честно: сколько блоков выдаст алгоритм, заранее не знает никто */}
            {minutesLeft !== null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('placement.minutesLeft', { count: minutesLeft })}
              </Typography>
            )}
          </Box>
        )}

        {/* Финиш мастера — отдельный экран, а не страница теста: у них разный
            смысл, и отчёт с оговорками на поздравлении лишний */}
        {wizardFinished && (
          <OnboardingFinish
            overallLevel={overallLevel}
            measuredLevels={measuredLevels}
            measurements={measurements}
            levelSetByTeacher={profile?.level_source === 'teacher'}
            name={studentName}
            onGoToDashboard={goToDashboard}
          />
        )}

        {/* Прогона нет: предлагаем начать либо объясняем, когда можно снова.
            В кабинете и на брошенном прогоне это по-прежнему нужный экран */}
        {!loading && !wizardFinished && (run === null || run.status !== 'in_progress') && (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                {measuredLevels !== null && (
                  <>
                    {run?.status === 'completed' && (
                      <Alert severity="success">
                        {profile?.level_source === 'teacher'
                          ? t('placement.finishedTeacherKept')
                          : t('placement.finished')}
                      </Alert>
                    )}
                    {/* Общий уровень — главное, за чем ученик шёл: крупно и
                        первым, а разбивка по навыкам под ним */}
                    {overallLevel !== null && (
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          {t('placement.overallLevelTitle')}
                        </Typography>
                        <Typography variant="h2" fontWeight={700} lineHeight={1}>
                          {overallLevel}
                        </Typography>
                      </Box>
                    )}

                    {/* Вторым по величине после уровня: цифра без этой оговорки
                        читается как оценка, а тест её не выдаёт. */}
                    <FairPlayNote />

                    <Typography variant="subtitle2">{t('placement.yourLevels')}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {PROFILE_SKILLS.map((skill) => {
                        const level = measuredLevels[skill]

                        return (
                          <Chip
                            key={skill}
                            label={`${t(`placement.skills.${skill}`)}: ${
                              level ?? skillMeasurementLabel(measurements[skill], t, { isProbe: PROBE_SKILLS.includes(skill) })
                            }`}
                            color={level ? 'primary' : 'default'}
                            variant={level ? 'filled' : 'outlined'}
                          />
                        )
                      })}
                    </Stack>

                    {/* Счёт по навыкам: ответ на «почему такой уровень». Какие
                        именно задания решены верно, ученику не показываем —
                        объяснять это ему не нужно, разбор его заданий лежит у
                        преподавателя. */}
                    {scoreRows.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2">{t('placement.yourScores')}</Typography>
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          {scoreRows.map(([skill, score]) => (
                            <Typography key={skill} variant="body2">
                              {t(`placement.skills.${skill}`)}:{' '}
                              {t('placement.scoreOf', { correct: score.correct, total: score.total })}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </>
                )}

                {run?.status === 'abandoned' && (
                  <Alert severity="info">
                    {retestAt === null ? t('placement.abandoned') : t('placement.abandonedLocked')}
                  </Alert>
                )}

                {/* Закрыт без единого измерения: без объяснения экран выглядел
                    бы как сброс в никуда. Признак берём из самого прогона —
                    срок пересдачи считается только по завершённым, и по нему
                    предупреждение показывалось бы всем брошенным подряд */}
                {run !== null
                  && run.status !== 'in_progress'
                  && run.computed_levels !== null
                  && Object.values(run.computed_levels).every((level) => level === null) && (
                  <Alert severity="warning">{t('placement.nothingMeasured')}</Alert>
                )}

                {/* В мастере это первый экран незнакомого человека: сначала
                    что за сервис и что сейчас будет, и только потом кнопка.
                    В кабинете так объяснять незачем — ученик уже внутри */}
                {fullscreen && measuredLevels === null ? (
                  <OnboardingWelcome t={t} />
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {t('placement.intro')}
                    </Typography>

                    {/* До первого прогона уровня ещё нет, и оговорка идёт здесь —
                        выше она рисуется только рядом с измеренным уровнем. */}
                    {measuredLevels === null && <FairPlayNote />}
                  </>
                )}

                {retestAt !== null ? (
                  <Alert severity="info">
                    {t('placement.retestAt', { date: formatDateDMY(retestAt) })}
                  </Alert>
                ) : (
                  <Box>
                    <Button
                      variant="contained"
                      size={fullscreen ? 'large' : 'medium'}
                      disabled={busy}
                      onClick={() => void start()}
                    >
                      {busy
                        ? t('common.loading')
                        : fullscreen && measuredLevels === null
                          ? t('placement.welcomeStart')
                          : t('placement.start')}
                    </Button>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Первая ступень: анкета. Цель и расписание спрашиваются до заданий —
            они не зависят от уровня, и ученик, бросивший тест на середине,
            всё равно оставляет о себе главное */}
        {!loading && run?.status === 'in_progress' && run.stage === 'goals' && (
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('placement.wizardGoalTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('placement.goalsHint')}
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('placement.goal')}
                    value={goals.goal}
                    disabled={busy}
                    onChange={(event) =>
                      setGoals((current) => ({
                        ...current,
                        goal: event.target.value as LearningGoal | '',
                        // Срок принадлежит цели: сменил цель — прежняя дата к
                        // ней уже не относится, и сервер её всё равно отобьёт.
                        goalDeadline: '',
                      }))
                    }
                  >
                    <MenuItem value="">{t('placement.goalNotChosen')}</MenuItem>
                    {LEARNING_GOALS.map((goal) => (
                      <MenuItem key={goal} value={goal}>
                        {t(`placement.goals.${goal}`)}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Дата — только у целей, у которых срок бывает: «разговорная
                      практика до 1 июня» не значит ничего */}
                  {goals.goal !== '' && GOALS_WITH_DEADLINE.includes(goals.goal) && (
                    <TextField
                      fullWidth
                      type="date"
                      size="small"
                      label={t('placement.goalDeadline')}
                      value={goals.goalDeadline}
                      disabled={busy}
                      slotProps={{ inputLabel: { shrink: true } }}
                      onChange={(event) =>
                        setGoals((current) => ({ ...current, goalDeadline: event.target.value }))
                      }
                    />
                  )}
                </Stack>

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label={t('placement.goalDetails')}
                  helperText={t('placement.goalDetailsHint')}
                  value={goals.goalDetails}
                  disabled={busy}
                  slotProps={{ htmlInput: { maxLength: 2000 } }}
                  onChange={(event) =>
                    setGoals((current) => ({ ...current, goalDetails: event.target.value }))
                  }
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('placement.daysPerWeek')}
                    value={goals.daysPerWeek}
                    disabled={busy}
                    onChange={(event) =>
                      setGoals((current) => ({ ...current, daysPerWeek: event.target.value }))
                    }
                  >
                    <MenuItem value="">{t('placement.intensityNotChosen')}</MenuItem>
                    {[1, 2, 3, 4, 5, 6, 7].map((days) => (
                      <MenuItem key={days} value={String(days)}>
                        {days}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('placement.minutesPerDay')}
                    value={goals.minutesPerDay}
                    disabled={busy}
                    onChange={(event) =>
                      setGoals((current) => ({ ...current, minutesPerDay: event.target.value }))
                    }
                  >
                    <MenuItem value="">{t('placement.intensityNotChosen')}</MenuItem>
                    {MINUTES_PER_DAY_OPTIONS.map((minutes) => (
                      <MenuItem key={minutes} value={String(minutes)}>
                        {minutes}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                {/* Когда именно удобно заниматься. «Три раза по часу» говорит
                    о нагрузке, а «вторник в 14» — о том, сойдутся ли расписания
                    вообще; раньше это выяснялось разговором на первом уроке */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('placement.preferredSlots')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t('placement.preferredSlotsHint')}
                  </Typography>

                  <Stack spacing={1.5}>
                    {goals.slots.map((slot, index) => (
                      <Stack key={index} direction="row" spacing={1} alignItems="center">
                        <TextField
                          select
                          size="small"
                          label={t('placement.slotWeekday')}
                          value={String(slot.weekday)}
                          disabled={busy}
                          sx={{ minWidth: 180 }}
                          onChange={(event) =>
                            setGoals((current) => ({
                              ...current,
                              slots: current.slots.map((row, position) =>
                                position === index
                                  ? { ...row, weekday: Number(event.target.value) }
                                  : row,
                              ),
                            }))
                          }
                        >
                          {WEEKDAYS.map((weekday) => (
                            <MenuItem key={weekday} value={String(weekday)}>
                              {t(`placement.weekdays.${weekday}`)}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          type="time"
                          size="small"
                          label={t('placement.slotTime')}
                          value={slot.time}
                          disabled={busy}
                          slotProps={{ inputLabel: { shrink: true } }}
                          sx={{ minWidth: 140 }}
                          onChange={(event) =>
                            setGoals((current) => ({
                              ...current,
                              slots: current.slots.map((row, position) =>
                                position === index ? { ...row, time: event.target.value } : row,
                              ),
                            }))
                          }
                        />

                        <Button
                          color="inherit"
                          size="small"
                          disabled={busy}
                          onClick={() =>
                            setGoals((current) => ({
                              ...current,
                              slots: current.slots.filter((_, position) => position !== index),
                            }))
                          }
                        >
                          {t('common.delete')}
                        </Button>
                      </Stack>
                    ))}

                    <Box>
                      <Button
                        size="small"
                        disabled={busy || goals.slots.length >= MAX_PREFERRED_SLOTS}
                        onClick={() =>
                          setGoals((current) => ({
                            ...current,
                            // Новая строка предлагает будний день и вечер: так
                            // занимается большинство, и человеку остаётся
                            // поправить, а не заполнить с нуля.
                            slots: [...current.slots, { weekday: 2, time: '18:00' }],
                          }))
                        }
                      >
                        {t('placement.addSlot')}
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  label={t('placement.interests')}
                  helperText={t('placement.interestsHint')}
                  value={goals.interests}
                  disabled={busy}
                  onChange={(event) =>
                    setGoals((current) => ({ ...current, interests: event.target.value }))
                  }
                />

                <Divider />

                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('placement.wizardSelfLevelTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('placement.selfLevelIntro')}
                  </Typography>
                </Box>

                {/* Прошлая самооценка и фактический результат — рядом с полем:
                    ученик, проходящий тест не впервые, видит, что о нём уже
                    известно, и не гадает, какое число называть. */}
                {(profile?.self_assessed_level != null || profile?.overall_level != null) && (
                  <Alert severity="info" icon={false}>
                    <Stack spacing={0.5}>
                      {profile?.self_assessed_level != null && (
                        <Typography variant="body2">
                          {t('placement.previousSelfAssessment', { level: profile.self_assessed_level })}
                        </Typography>
                      )}
                      {profile?.overall_level != null && (
                        <Typography variant="body2">
                          {t('placement.previousMeasured', {
                            level: profile.overall_level,
                            date: profile.assessed_at !== null ? formatDateDMY(profile.assessed_at) : '—',
                          })}
                        </Typography>
                      )}
                    </Stack>
                  </Alert>
                )}

                <TextField
                  select
                  size="small"
                  label={t('placement.selfLevel')}
                  helperText={t('placement.selfLevelHint')}
                  value={goals.selfLevel}
                  disabled={busy}
                  onChange={(event) =>
                    setGoals((current) => ({
                      ...current,
                      selfLevel: event.target.value as ProfileLevel | '',
                    }))
                  }
                  sx={{ maxWidth: 280 }}
                >
                  <MenuItem value="">{t('placement.selfLevelUnknown')}</MenuItem>
                  {PROFILE_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Обязательных полей нет: анкету можно проскочить целиком.
                    Требовать здесь цель значило бы запирать тест за анкетой,
                    которую ученик пришёл пропустить */}
                <Box>
                  <Button variant="contained" disabled={busy} onClick={() => void submitGoals()}>
                    {busy ? t('common.loading') : t('placement.toExercises')}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Вторая и третья ступени: задания блока */}
        {!loading && run?.status === 'in_progress' && run.stage !== 'goals' && (
          <Stack spacing={2}>
            {items.length === 0 && (
              <Alert severity="info">{t('placement.noItems')}</Alert>
            )}

            {items.length > 0 && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={(filledCount / items.length) * 100}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('placement.filledOf', { filled: filledCount, total: items.length })}
                </Typography>
              </Box>
            )}

            {items.map((item, index) => (
              <Card key={item.id}>
                <CardContent>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ flex: 1 }}>
                        {/* Номер по порядку, а не название задания: заголовок
                            иногда выдаёт, о чём задание, и подсказывает */}
                        {t('placement.itemNumber', { number: index + 1, total: items.length })}
                      </Typography>
                      {item.answered
                        ? <Chip size="small" color="success" label={t('placement.answered')} />
                        : isFilled(answers[item.id])
                          ? <Chip size="small" color="primary" variant="outlined" label={t('placement.filled')} />
                          : <Chip size="small" variant="outlined" label={t('placement.notFilled')} />}
                    </Box>

                    {item.exercise.instructions && (
                      <Typography variant="body2" color="text.secondary">
                        {item.exercise.instructions}
                      </Typography>
                    )}

                    <ExerciseRenderer
                      type={item.exercise.type}
                      content={item.exercise.content}
                      answers={answers[item.id] ?? {}}
                      onChange={(next) => setAnswers((current) => ({ ...current, [item.id]: next }))}
                      readOnly={item.answered || busy}
                      // «Соответствия» в тесте собираются на клиенте: ручка
                      // match-attempt для теста закрыта — она отвечала «верно
                      // или нет» на каждую пару и выдавала эталон по биту за
                      // запрос. Без runId рендерер просто соединяет пары, а
                      // проверка идёт на сдаче.
                      runId={
                        item.exercise.type === 'matching'
                          ? undefined
                          : item.exercise_run_id ?? undefined
                      }
                    />

                  </Stack>
                </CardContent>
              </Card>
            ))}

            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {t('placement.blockHint')}
                  </Typography>

                  {/* Одна кнопка на весь блок: задания сдаются разом, потому
                      что сдавать каждое отдельно человек забывает */}
                  <Button
                    variant="contained"
                    disabled={busy}
                    onClick={() =>
                      finishBlock(run.stage === 'adaptive' ? advanceAfterBlock : completeAfterBlock)
                    }
                  >
                    {busy
                      ? t('common.loading')
                      : run.stage === 'adaptive'
                        ? t('placement.answerAndContinue')
                        : t('placement.answerAndFinish')}
                  </Button>

                  <Button
                    color="inherit"
                    disabled={busy}
                    onClick={() => setLeaveOpen(true)}
                  >
                    {t('placement.quit')}
                  </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" color="text.secondary">
                  {t('placement.partialHint')}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        )}
        {/* Выход из теста: цена действия названа, и по умолчанию предлагается
            сохранить — уйти, потеряв решённое, должно быть отдельным выбором,
            а не тем, что происходит само */}
        <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>{t('placement.leaveTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{t('placement.leaveExplain')}</Typography>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button color="inherit" disabled={busy} onClick={() => setLeaveOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button color="inherit" disabled={busy} onClick={() => void abandon()}>
              {t('placement.leaveWithoutSaving')}
            </Button>
            <Button variant="contained" disabled={busy} onClick={saveAndLeave}>
              {t('placement.saveResult')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  )

  if (!fullscreen) {
    return <DashboardLayout>{content}</DashboardLayout>
  }

  // Тест уже закрыт — уходим в кабинет молча: гейт снят сам, и писать ученику
  // отказ от мастера здесь было бы неправдой. Пока тест идёт, уход — это
  // «продолжить позже»: прогон остаётся на сервере, гейт снимается.
  const finished = run !== null && run.status !== 'in_progress'

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <OnboardingHeader
        leaveLabel={
          wizardFinished
            ? t('placement.finishCta')
            : finished
              ? t('placement.toDashboard')
              : t('placement.skipWizard')
        }
        leaveHint={finished ? undefined : t('placement.skipWizardHint')}
        leaveDisabled={!finished && busy}
        onLeave={finished ? goToDashboard : () => void leaveOnboarding()}
        onLogout={() => void handleLogout()}
      />

      {/* Шапка лендинга закреплена сверху — контенту нужен её просвет. */}
      <Box sx={{ pt: '72px' }}>{content}</Box>
    </Box>
  )
}
